import React from 'react';
import { Grid, Typography, Button, Box } from '@mui/material';
import { useFormikContext } from 'formik';
import InputField from '../../../../components/formFields/InputField';
import contratoFormModel from '../formik/contratoFormModel';
import { TypeContractOptions } from '../data/data';
import AutocompleteField from 'components/formFields/autocompleteField';
import Editor, { 
  EditorProvider, 
  Toolbar, 
  BtnBold, 
  BtnItalic, 
  BtnUnderline, 
  BtnStrikeThrough,
  BtnNumberedList, 
  BtnBulletList,
  Separator,
  BtnStyles,
  createButton
} from 'react-simple-wysiwyg';

// Icons for formatting
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';

const BtnAlignLeft = createButton('Align left', <FormatAlignLeftIcon fontSize="small" />, 'justifyLeft');
const BtnAlignCenter = createButton('Align center', <FormatAlignCenterIcon fontSize="small" />, 'justifyCenter');
const BtnAlignRight = createButton('Align right', <FormatAlignRightIcon fontSize="small" />, 'justifyRight');

const BtnColor = () => (
  <input
    type="color"
    title="Color de texto"
    defaultValue="#000000"
    style={{ width: '24px', height: '24px', padding: 0, border: 'none', background: 'none', cursor: 'pointer', margin: '0 4px', alignSelf: 'center' }}
    onChange={(e) => {
      document.execCommand('foreColor', false, e.target.value);
    }}
  />
);

export const FormContrato = () => {
  const { values, setFieldValue, touched, errors } = useFormikContext<any>();
  const { formField: { name, typeContract, content } } = contratoFormModel;

  // Variables dinámicas para el contrato
  const variables = [
    { label: 'Empleado', value: '[Nombre Empleado]', icon: <PersonIcon fontSize="small" /> },
    { label: 'Identificación', value: '[Identificación]', icon: <BadgeIcon fontSize="small" /> },
    { label: 'Cargo', value: '[Cargo]', icon: <WorkIcon fontSize="small" /> },
    { label: 'Salario', value: '[Salario]', icon: <AttachMoneyIcon fontSize="small" /> },
    { label: 'Fecha Inicio', value: '[Fecha Inicio]', icon: <CalendarMonthIcon fontSize="small" /> },
  ];

  const handleInsertVariable = (variable: string) => {
    // Intenta insertar el texto en la posición del cursor usando execCommand
    document.execCommand('insertText', false, variable);
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{xs: 12, md: values[typeContract.name]?.value === 'otro' ? 6 : 12}}>
        <AutocompleteField  
          name={typeContract.name} 
          label={typeContract.label} 
          options={TypeContractOptions} 
          element={typeContract.name}
          fullWidth 
          onChange={(e: any, v: any) => {
            if (v && v.value !== 'otro') {
              setFieldValue(name.name, v.label);
            } else if (v && v.value === 'otro') {
              setFieldValue(name.name, '');
            }
          }}
        />
      </Grid>
      {values[typeContract.name]?.value === 'otro' && (
        <Grid size={{xs: 12, md: 6}}>
          <InputField 
            name={name.name} 
            label={name.label} 
            fullWidth 
          />
        </Grid>
      )}
      <Grid size={{xs: 12 }}>
        <Typography variant="subtitle2" gutterBottom>{content.label}</Typography>
        
        {/* Variables Toolbar */}
        <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {variables.map((v) => (
            <Button 
              key={v.value}
              size="small" 
              variant="outlined" 
              sx={{ textTransform: 'none', py: 0, px: 1, color: 'text.secondary', borderColor: '#ccc', bgcolor: '#fff' }}
              startIcon={v.icon}
              onClick={() => handleInsertVariable(v.value)}
              onMouseDown={(e) => e.preventDefault()} // Evita perder el foco del editor
            >
              {v.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ '.rsw-editor': { minHeight: 300, bgcolor: '#fff' } }}>
          <EditorProvider>
            <Editor 
              value={values[content.name] || ''} 
              onChange={(e) => setFieldValue(content.name, e.target.value)}
            >
              <Toolbar>
                <BtnBold />
                <BtnItalic />
                <BtnUnderline />
                <BtnStrikeThrough />
                <Separator />
                <BtnNumberedList />
                <BtnBulletList />
                <Separator />
                <BtnAlignLeft />
                <BtnAlignCenter />
                <BtnAlignRight />
                <Separator />
                <BtnColor />
                <Separator />
                <BtnStyles />
              </Toolbar>
            </Editor>
          </EditorProvider>
        </Box>
        
        {touched[content.name] && errors[content.name] && (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
            {errors[content.name] as string}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};
